import { Children, createContext, isValidElement, type ReactNode, useCallback, useContext, useState } from "react";
import { Badge, Body1, Button, type ButtonProps, Display2, getTokenValue, isWeb, KeyboardAwareScrollView, KeyboardStickyView, useSafeAreaInsets, YStack } from "@medbillai/ui";
import type { ShallowDistributiveOmit } from "@medbillai/utils";
import { BackgroundRadialGrantedGradient } from "../BackgroundRadialGrantedGradient";
import { NewGLogo } from "../local-images";
import { OnboardingProgressHeader } from "./OnboardingProgressHeader";
import { useNativePadding } from "./useNativePadding";

// Resolve at module-load — Tamagui's native `Image` doesn't reliably
// parse `$`-prefixed size tokens, so pass it a concrete number.
const LOGO_SIZE = Number(getTokenValue("$si5", "size"));
const FOOTER_KEYBOARD_OFFSET = Number(getTokenValue("$-sp2", "space"));
// One token for the footer slot gap so the layout and computeOpenedOffset can't drift.
const FOOTER_SLOT_GAP_TOKEN = "$sp2" as const;
const SLOT_GAP = Number(getTokenValue(FOOTER_SLOT_GAP_TOKEN, "space"));
// onLayout re-fires on sub-pixel jitter; ignore deltas below this.
const SUBPIXEL_EPSILON = 0.5;

// Value/setter split so the Footer (producer) only subscribes to the stable
// setter — publishing a new height via onLayout doesn't loop back into its
// render and re-fire onLayout.
const OnboardingPrimaryHeightContext = createContext<number>(0);
const OnboardingPrimaryHeightSetterContext = createContext<(height: number) => void>(() => {});
type TextButtonProps = Extract<ButtonProps, {
  children: ReactNode;
}>;
type OnboardingButtonProps = ShallowDistributiveOmit<TextButtonProps, "variant" | "fillWidth" | "size">;
type OnboardingNavProps = {
  /** 0–100. When omitted, the progress bar is hidden. */
  progress?: number;
  /** Native only — web always shows the wordmark. Pass to render a back button. */
  onBackPress?: () => void;
  /** Accessible title; not rendered visibly. */
  title?: string;
  /** Web only — render the Granted wordmark in the left slot. @default true */
  showLogo?: boolean;
};
type OnboardingHeaderBaseProps = {
  title: string;
  subtitle?: string;
  /** Flex alignment and `textAlign` for title/subtitle. @default "center" */
  alignment?: "center" | "left";
  testID?: string;
};
type OnboardingHeaderProps = OnboardingHeaderBaseProps & ({
  showLogo?: boolean;
  badge?: never;
} | {
  showLogo?: never;
  badge?: string;
});
type OnboardingContentProps = {
  children: ReactNode;
};
type OnboardingFooterProps = {
  /** Web-only fade; matches the opacity/transition pattern used for content. */
  isFading?: boolean;
  children: ReactNode;
};
type OnboardingPrimaryActionProps = OnboardingButtonProps & {
  /** @default "primary" */
  variant?: "primary" | "brand";
};
type OnboardingDisclaimerProps = {
  children: ReactNode;
};

/**
 * Partitions footer children by PrimaryAction identity.
 *
 * Returns items before the primary in `above` (never floated), the primary
 * element itself, and items after it in `below` (measured, covered by keyboard).
 * When no primary is found, all items land in `above` and the footer renders
 * flat with no sticky behaviour.
 */
export function partitionFooterChildren(children: ReactNode): {
  above: ReactNode[];
  primary: ReactNode | null;
  below: ReactNode[];
} {
  const items = Children.toArray(children);
  const primaryIndex = items.findIndex(c => isValidElement(c) && c.type === OnboardingPrimaryAction);
  if (primaryIndex === -1) {
    return {
      above: items,
      primary: null,
      below: []
    };
  }
  return {
    above: items.slice(0, primaryIndex),
    primary: items[primaryIndex] ?? null,
    below: items.slice(primaryIndex + 1)
  };
}

/**
 * Computes the `KeyboardStickyView` opened offset for the primary button.
 *
 * When content sits below the primary, the primary's natural resting position
 * is raised by `spaceBelow + SLOT_GAP`; those are added back so the button
 * parks at the same distance above the keyboard regardless of below-content.
 */
export function computeOpenedOffset(spaceBelow: number, hasBelow: boolean): number {
  return FOOTER_KEYBOARD_OFFSET + (hasBelow ? spaceBelow + SLOT_GAP : 0);
}

// Deduped onLayout height; setter is stable, so it's safe to publish via context.
function useDedupedHeight(): [number, (next: number) => void] {
  const [height, setHeight] = useState(0);
  const set = useCallback((next: number) => {
    setHeight(prev => Math.abs(prev - next) < SUBPIXEL_EPSILON ? prev : next);
  }, []);
  return [height, set];
}

/**
 * Nav slot. Callers should always render `<OnboardingScreen.Nav />` so the
 * slot reserves the same `$si7` vertical space whether or not a progress
 * bar or back button is shown. On web the Granted wordmark renders by
 * default — pass `showLogo={false}` on screens that should suppress it.
 */
function OnboardingNav({
  progress,
  onBackPress,
  title,
  showLogo
}: OnboardingNavProps) {
  return <OnboardingProgressHeader progress={progress} onBackPress={onBackPress} title={title} showLogo={showLogo} data-sentry-element="OnboardingProgressHeader" data-sentry-component="OnboardingNav" data-sentry-source-file="OnboardingScreen.tsx" />;
}

/**
 * Header region. Designed to be rendered as the first child of
 * `<OnboardingScreen.Content>` — horizontal padding and the top inset are
 * owned by `Content`, so rendering `<Header>` outside of `<Content>` is
 * unsupported.
 */
function OnboardingHeader(props: OnboardingHeaderProps) {
  const {
    title,
    subtitle,
    alignment = "center",
    testID
  } = props;
  const isLeft = alignment === "left";
  const textAlign = isLeft ? "left" : "center";
  return <YStack paddingBottom="$sp8" gap="$sp1.5" alignItems={isLeft ? "flex-start" : "center"} testID={testID} data-sentry-element="YStack" data-sentry-component="OnboardingHeader" data-sentry-source-file="OnboardingScreen.tsx">
      {props.showLogo ? <NewGLogo height={LOGO_SIZE} width={LOGO_SIZE} /> : null}
      {props.badge ? <Badge status="positive" variant="filled" size="medium">
          {props.badge}
        </Badge> : null}
      <Display2 textAlign={textAlign} data-sentry-element="Display2" data-sentry-source-file="OnboardingScreen.tsx">{title}</Display2>
      {subtitle ? <Body1 color="$textSubtle" textAlign={textAlign}>
          {subtitle}
        </Body1> : null}
    </YStack>;
}

/**
 * Content region. Owns the scroll region and body padding. On web, the
 * outer wrapper IS the scroll region (short content sits at natural size,
 * long content shrinks and scrolls internally so the footer stays pinned).
 * On native, wraps a `ScrollView` that only scrolls when content overflows.
 */
function OnboardingContent({
  children
}: OnboardingContentProps) {
  if (isWeb) {
    return <YStack width="100%" maxWidth={456} alignSelf="center" maxHeight="100%" style={{
      overflowY: "auto"
    }} flexShrink={1} flexBasis="auto" minHeight={0}>
        <ContentBody>{children}</ContentBody>
      </YStack>;
  }
  return <NativeContentScrollRegion data-sentry-element="NativeContentScrollRegion" data-sentry-component="OnboardingContent" data-sentry-source-file="OnboardingScreen.tsx">{children}</NativeContentScrollRegion>;
}
function ContentBody({
  children
}: {
  children: ReactNode;
}) {
  return <YStack flex={1} paddingTop="$sp4" paddingBottom="$sp6" paddingHorizontal="$sp2.5" data-sentry-element="YStack" data-sentry-component="ContentBody" data-sentry-source-file="OnboardingScreen.tsx">
      {children}
    </YStack>;
}
function NativeContentScrollRegion({
  children
}: {
  children: ReactNode;
}) {
  const primaryHeight = useContext(OnboardingPrimaryHeightContext);
  const insets = useSafeAreaInsets();

  // KAS computes scroll using useWindowDimensions().height (full-screen, incl.
  // top inset / Dynamic Island), so its "visible area above keyboard" is taller
  // than the actual app area by `insets.top`. Add the inset back so the offset
  // expresses the gap from keyboard top to where the input must clear; also add
  // |FOOTER_KEYBOARD_OFFSET| since KeyboardStickyView lifts the primary that gap
  // *above* the keyboard top.
  const bottomOffset = primaryHeight + insets.top - FOOTER_KEYBOARD_OFFSET;
  return <KeyboardAwareScrollView style={{
    flex: 1
  }} bottomOffset={bottomOffset} bounces={false} overScrollMode="never" contentContainerStyle={{
    flexGrow: 1
  }} data-sentry-element="KeyboardAwareScrollView" data-sentry-component="NativeContentScrollRegion" data-sentry-source-file="OnboardingScreen.tsx">
      <ContentBody data-sentry-element="ContentBody" data-sentry-source-file="OnboardingScreen.tsx">{children}</ContentBody>
    </KeyboardAwareScrollView>;
}

// Shared action-strip container: paddings, slot gap, and the web fade transition.
function FooterStrip({
  isFading,
  children
}: {
  isFading?: boolean;
  children: ReactNode;
}) {
  return <YStack paddingHorizontal="$sp2.5" gap={FOOTER_SLOT_GAP_TOKEN} opacity={isFading ? 0 : 1} transition="300ms ease" data-sentry-element="YStack" data-sentry-component="FooterStrip" data-sentry-source-file="OnboardingScreen.tsx">
      {children}
    </YStack>;
}

// if above-primary content and a keyboard are both visible on a very short screen the floating button can overlap it — no current call site does this
function OnboardingFooter({
  children,
  isFading
}: OnboardingFooterProps) {
  const setPrimaryHeight = useContext(OnboardingPrimaryHeightSetterContext);
  const [spaceBelow, handleBelowLayout] = useDedupedHeight();
  if (isWeb) {
    return <YStack width="100%" maxWidth={456} alignSelf="center">
        <FooterStrip isFading={isFading}>{children}</FooterStrip>
      </YStack>;
  }
  const {
    above,
    primary,
    below
  } = partitionFooterChildren(children);
  const hasBelow = below.length > 0;
  const openedOffset = computeOpenedOffset(spaceBelow, hasBelow);
  return <FooterStrip isFading={isFading} data-sentry-element="FooterStrip" data-sentry-component="OnboardingFooter" data-sentry-source-file="OnboardingScreen.tsx">
      {above}
      {primary !== null ? <KeyboardStickyView offset={{
      opened: openedOffset
    }}>
          <YStack onLayout={e => setPrimaryHeight(e.nativeEvent.layout.height)}>
            {primary}
          </YStack>
        </KeyboardStickyView> : null}
      {hasBelow ? <YStack gap={FOOTER_SLOT_GAP_TOKEN} onLayout={e => handleBelowLayout(e.nativeEvent.layout.height)}>
          {below}
        </YStack> : null}
    </FooterStrip>;
}
function OnboardingPrimaryAction({
  variant = "primary",
  ...rest
}: OnboardingPrimaryActionProps) {
  return <Button variant={variant} size="large" fillWidth {...rest} data-sentry-element="Button" data-sentry-component="OnboardingPrimaryAction" data-sentry-source-file="OnboardingScreen.tsx" />;
}
function OnboardingSecondaryAction(props: OnboardingButtonProps) {
  return <Button variant="secondary" size="large" fillWidth {...props} data-sentry-element="Button" data-sentry-component="OnboardingSecondaryAction" data-sentry-source-file="OnboardingScreen.tsx" />;
}
function OnboardingTertiaryAction(props: OnboardingButtonProps) {
  return <Button variant="tertiary" size="large" fillWidth {...props} data-sentry-element="Button" data-sentry-component="OnboardingTertiaryAction" data-sentry-source-file="OnboardingScreen.tsx" />;
}
function OnboardingDisclaimer({
  children
}: OnboardingDisclaimerProps) {
  return <YStack alignItems="center" data-sentry-element="YStack" data-sentry-component="OnboardingDisclaimer" data-sentry-source-file="OnboardingScreen.tsx">{children}</YStack>;
}
export type OnboardingScreenProps = {
  /** Render `BackgroundRadialGrantedGradient` fullscreen behind the content. */
  showBackgroundGradient?: boolean;
  children: ReactNode;
};
function OnboardingScreenRoot({
  showBackgroundGradient,
  children
}: OnboardingScreenProps) {
  // Safe-area padding for native (all zeros on web). Bottom inset collapses
  // to 0 while the keyboard is visible so the KeyboardStickyView'd footer
  // stays flush with the keyboard top; Tamagui's `transition` animates that
  // change.
  const padding = useNativePadding();
  const [primaryHeight, setHeight] = useDedupedHeight();
  return <OnboardingPrimaryHeightContext.Provider value={primaryHeight} data-sentry-element="OnboardingPrimaryHeightContext.Provider" data-sentry-component="OnboardingScreenRoot" data-sentry-source-file="OnboardingScreen.tsx">
      <OnboardingPrimaryHeightSetterContext.Provider value={setHeight} data-sentry-element="OnboardingPrimaryHeightSetterContext.Provider" data-sentry-source-file="OnboardingScreen.tsx">
        <YStack flex={1} transition="200ms easeCubic" {...padding} {...isWeb ? {
        paddingTop: "$sp2"
      } : {}} data-sentry-element="YStack" data-sentry-source-file="OnboardingScreen.tsx">
          {showBackgroundGradient ? <BackgroundRadialGrantedGradient fullscreen /> : null}
          {children}
        </YStack>
      </OnboardingPrimaryHeightSetterContext.Provider>
    </OnboardingPrimaryHeightContext.Provider>;
}
export const OnboardingScreen = Object.assign(OnboardingScreenRoot, {
  Nav: OnboardingNav,
  Header: OnboardingHeader,
  Content: OnboardingContent,
  Footer: OnboardingFooter,
  PrimaryAction: OnboardingPrimaryAction,
  SecondaryAction: OnboardingSecondaryAction,
  TertiaryAction: OnboardingTertiaryAction,
  Disclaimer: OnboardingDisclaimer
});
export type { OnboardingHeaderProps, OnboardingNavProps, OnboardingPrimaryActionProps };